/**
 * Native HTML5 drag-and-drop reorder hook.
 *
 * Why native rather than @dnd-kit / react-beautiful-dnd: we have at most
 * 5-20 rows per list (Principal Particulars, Key Features) so the
 * pointer/keyboard a11y story of those libraries is overkill. Native DnD
 * is one dependency-free file, works on all modern browsers, and the
 * grip-handle pattern below keeps it accessible enough (button is
 * focusable and labelled).
 *
 * Usage:
 *
 *   const { handleProps, rowProps, draggingIndex, overIndex } =
 *     useDraggableReorder(list, setList);
 *
 *   list.map((item, i) => (
 *     <div {...rowProps(i)}>
 *       <button {...handleProps(i)} aria-label="Reorder">
 *         <GripVertical />
 *       </button>
 *       ...row content...
 *     </div>
 *   ))
 *
 * The handle is the only element with ``draggable=true`` so the row's
 * inputs still accept text selection / paste / etc. The row itself
 * registers ``dragover`` + ``drop`` so we can compute the target index
 * from anywhere along the row.
 */
import { useState, useCallback } from "react";

export function useDraggableReorder(list, setList) {
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const reset = useCallback(() => {
    setDraggingIndex(null);
    setOverIndex(null);
  }, []);

  const move = useCallback(
    (from, to) => {
      if (from == null || to == null) return;
      if (from === to) return;
      const next = [...list];
      const [moved] = next.splice(from, 1);
      // After removing the source, ``to`` is still the right insertion
      // index — splicing collapses the indices on both sides.
      next.splice(to, 0, moved);
      setList(next);
    },
    [list, setList],
  );

  // The drag handle (must be the only draggable=true element so text
  // selection in the row's inputs still works).
  const handleProps = useCallback(
    (index) => ({
      draggable: true,
      "aria-label": "Drag to reorder",
      onDragStart: (e) => {
        setDraggingIndex(index);
        // Required for Firefox — without setData the dragstart event
        // silently aborts.
        e.dataTransfer?.setData("text/plain", String(index));
        e.dataTransfer.effectAllowed = "move";
      },
      onDragEnd: reset,
    }),
    [reset],
  );

  // The row wrapper — only listens for dragover / drop so we can hit-test
  // any horizontal position of the row.
  const rowProps = useCallback(
    (index) => ({
      onDragOver: (e) => {
        if (draggingIndex == null) return;
        // preventDefault is required to register as a valid drop target.
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
        if (overIndex !== index) setOverIndex(index);
      },
      onDragLeave: () => {
        if (overIndex === index) setOverIndex(null);
      },
      onDrop: (e) => {
        e.preventDefault();
        move(draggingIndex, index);
        reset();
      },
    }),
    [draggingIndex, overIndex, move, reset],
  );

  return { handleProps, rowProps, draggingIndex, overIndex };
}
